import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  MagnifyingGlass,
  ShieldCheck,
  IdentificationCard,
  Link as LinkIcon,
  CaretRight,
  CheckCircle,
  Warning,
  XCircle,
  Pill,
  House,
  User,
  Tag,
  DownloadSimple,
  MapPin,
  Phone,
  Stethoscope,
  ListNumbers,
  UserCircle,
} from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { api, getNameSlug } from "../lib/api";

const FAQ_DATA = [
  {
    q: "Can I look up a DEA number using an NPI number?",
    a: "Yes, in some cases. When a provider includes their DEA registration number in their NPPES profile, it appears in the NPI database and can be retrieved via NPI lookup. However, not all providers list their DEA number in NPPES.",
  },
  {
    q: "Why might a DEA number not appear when I look up an NPI?",
    a: "Providers are not required to include their DEA number in their NPPES profile. Many prescribers have valid DEA registrations that simply are not linked to their NPI record in the public database.",
  },
  {
    q: "Is a DEA number the same as an NPI number?",
    a: "No. They are issued by different federal agencies for different purposes. An NPI is issued by CMS and identifies a provider in the healthcare system. A DEA number is issued by the Drug Enforcement Administration and specifically authorises a provider to handle controlled substances.",
  },
  {
    q: "How do I verify a DEA number directly?",
    a: "DEA registrations can be verified through the DEA Diversion Control Division at deadiversion.usdoj.gov. Pharmacies can also verify via the DEA's online registration lookup.",
  },
  {
    q: "Do all healthcare providers have DEA numbers?",
    a: "No. Only providers who prescribe, dispense, or administer controlled substances need a DEA number. Many providers such as radiologists, pathologists, and certain therapists do not hold DEA registrations.",
  },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ_DATA.map((faq) => ({
    "@type": "Question",
    name: faq.q,
    acceptedAnswer: { "@type": "Answer", text: faq.a },
  })),
};

// Extract provider info from raw NPI result
const extractProviderInfo = (provider) => {
  const basic = provider.basic || {};
  const isIndividual = provider.enumeration_type === "NPI-1";
  const name = isIndividual
    ? `${basic.first_name || ""} ${basic.last_name || ""}`.trim() +
      (basic.credential ? `, ${basic.credential}` : "")
    : basic.organization_name || "Unknown Provider";
  const nameOnly = isIndividual
    ? `${basic.first_name || ""} ${basic.last_name || ""}`.trim()
    : basic.organization_name || "Unknown";
  const specialty =
    provider.taxonomies?.find((t) => t.primary)?.desc ||
    provider.taxonomies?.[0]?.desc ||
    "";
  const addr =
    provider.addresses?.find((a) => a.address_purpose === "LOCATION") ||
    provider.addresses?.[0];
  const phone = addr?.telephone_number;
  const location = addr
    ? `${addr.address_1 || ""}${addr.city ? `, ${addr.city}` : ""}${addr.state ? `, ${addr.state}` : ""} ${addr.postal_code || ""}`.trim()
    : "";
  const identifiers = provider.identifiers || [];
  const deaEntry = identifiers.find(
    (id) =>
      (id.desc && id.desc.toUpperCase().includes("DEA")) ||
      (id.code && id.code === "01")
  );
  const enumerationDate = basic.enumeration_date || "";
  const yearsRegistered = enumerationDate
    ? new Date().getFullYear() - new Date(enumerationDate).getFullYear()
    : null;

  return {
    npi: String(provider.number),
    name,
    nameOnly,
    specialty,
    location,
    phone,
    dea: deaEntry?.identifier || null,
    yearsRegistered,
    enumerationDate,
    slug: getNameSlug(nameOnly),
  };
};

// Reusable provider info card
const ProviderInfoCard = ({ info }) => (
  <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200" data-testid="provider-info-card">
    <div className="flex items-start justify-between">
      <div className="space-y-1.5 flex-1">
        <Link
          to={`/provider/${info.npi}/${info.slug}`}
          className="font-medium text-navy hover:text-teal transition-colors text-sm"
          title={`View full profile for ${info.nameOnly}`}
        >
          {info.name}
        </Link>
        {info.specialty && (
          <p className="text-xs text-teal flex items-center gap-1">
            <Stethoscope className="w-3 h-3" aria-hidden="true" />
            {info.specialty}
          </p>
        )}
        {info.location && (
          <p className="text-xs text-slate-500 flex items-center gap-1">
            <MapPin className="w-3 h-3" aria-hidden="true" />
            {info.location}
          </p>
        )}
        {info.phone && (
          <p className="text-xs text-slate-500 flex items-center gap-1">
            <Phone className="w-3 h-3" aria-hidden="true" />
            {info.phone}
          </p>
        )}
      </div>
      <div className="text-right ml-4 shrink-0">
        <p className="text-xs text-slate-400 font-mono">NPI: {info.npi}</p>
        {info.yearsRegistered !== null && (
          <p className="text-xs text-slate-500 mt-1">{info.yearsRegistered} yrs registered</p>
        )}
      </div>
    </div>
  </div>
);

const DeaNpiLookup = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("single"); // single | bulk | name
  const [npiInput, setNpiInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Bulk lookup state
  const [bulkInput, setBulkInput] = useState("");
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResults, setBulkResults] = useState([]);
  const [bulkProgress, setBulkProgress] = useState(0);

  // Name search state
  const [nameInput, setNameInput] = useState("");
  const [nameLoading, setNameLoading] = useState(false);
  const [nameResults, setNameResults] = useState([]);

  // --- Single NPI Lookup ---
  const handleLookup = useCallback(async (npiOverride) => {
    const npi = (npiOverride || npiInput).trim();
    if (!npi || npi.length !== 10 || !/^\d{10}$/.test(npi)) {
      setResult({ status: "invalid" });
      return;
    }

    setLoading(true);
    setResult(null);
    try {
      const data = await api.getProvider(npi);
      const provider = data?.result;
      if (!provider) {
        setResult({ status: "invalid" });
        return;
      }

      const info = extractProviderInfo(provider);

      if (info.dea) {
        setResult({ status: "found", ...info });
      } else {
        setResult({ status: "not_found", ...info });
      }
    } catch {
      setResult({ status: "invalid" });
    } finally {
      setLoading(false);
    }
  }, [npiInput]);

  // Auto-fill from URL param
  useEffect(() => {
    const npiParam = searchParams.get("npi");
    if (npiParam && /^\d{10}$/.test(npiParam)) {
      setNpiInput(npiParam);
      handleLookup(npiParam);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Bulk Lookup ---
  const handleBulkLookup = async () => {
    const lines = bulkInput
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => /^\d{10}$/.test(l));
    if (lines.length === 0) return;
    const npis = lines.slice(0, 10);

    setBulkLoading(true);
    setBulkResults([]);
    setBulkProgress(0);

    const results = [];
    for (let i = 0; i < npis.length; i++) {
      setBulkProgress(i + 1);
      try {
        const data = await api.getProvider(npis[i]);
        const provider = data?.result;
        if (provider) {
          const info = extractProviderInfo(provider);
          results.push({ ...info, status: info.dea ? "found" : "not_found" });
        } else {
          results.push({ npi: npis[i], name: "Not Found", specialty: "", dea: null, status: "invalid" });
        }
      } catch {
        results.push({ npi: npis[i], name: "Error", specialty: "", dea: null, status: "invalid" });
      }
    }
    setBulkResults(results);
    setBulkLoading(false);
  };

  const downloadCSV = () => {
    const headers = ["NPI Number", "Provider Name", "Specialty", "DEA Number", "Status"];
    const rows = bulkResults.map((r) => [
      r.npi,
      `"${(r.name || "").replace(/"/g, '""')}"`,
      `"${(r.specialty || "").replace(/"/g, '""')}"`,
      r.dea || "",
      r.status === "found" ? "DEA Found" : r.status === "not_found" ? "No DEA on File" : "Invalid NPI",
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "dea_lookup_results.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  // --- Name Search ---
  const handleNameSearch = async () => {
    const name = nameInput.trim();
    if (!name) return;

    setNameLoading(true);
    setNameResults([]);
    try {
      const parts = name.split(/\s+/);
      const params = {};
      if (parts.length >= 2) {
        params.first_name = parts[0];
        params.last_name = parts.slice(1).join(" ");
      } else {
        params.last_name = parts[0];
      }
      params.limit = 5;
      const data = await api.searchProviders(params);
      const results = (data.results || []).map((p) => {
        const info = extractProviderInfo(p);
        return { ...info, status: info.dea ? "found" : "not_found" };
      });
      setNameResults(results);
    } catch {
      setNameResults([]);
    } finally {
      setNameLoading(false);
    }
  };

  const tabs = [
    { id: "single", label: "Single NPI Lookup", icon: IdentificationCard },
    { id: "bulk", label: "Bulk Lookup", icon: ListNumbers },
    { id: "name", label: "Search by Name", icon: UserCircle },
  ];

  return (
    <>
      <Helmet>
        <title>
          DEA NPI Lookup — Free DEA Number Cross-Reference | NPI Lookup
        </title>
        <meta
          name="description"
          content="Free DEA NPI lookup. Cross-reference a DEA number against an NPI number in the NPPES national registry — verify DEA registrations for any healthcare provider instantly."
        />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://npilookup.com/" },
            { "@type": "ListItem", "position": 2, "name": "DEA-NPI Lookup" }
          ]
        })}</script>
      </Helmet>

      <div className="bg-white min-h-screen">
        {/* Hero */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-6 text-center">
          <h1
            className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-navy"
            data-testid="dea-hero-title"
          >
            Find a DEA Number Using an NPI
          </h1>
          <p className="mt-4 text-slate-500 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            Enter any NPI number below to check if a DEA registration number is
            associated with this provider in the NPPES database.
          </p>
        </section>

        {/* Tab Selector */}
        <section className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
          <div className="flex gap-1 bg-slate-100 rounded-xl p-1" data-testid="dea-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? "bg-white text-navy shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
                data-testid={`dea-tab-${tab.id}`}
                title={tab.label}
              >
                <tab.icon className="w-4 h-4" aria-hidden="true" />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>
        </section>

        {/* ============ TAB: Single NPI Lookup ============ */}
        {activeTab === "single" && (
          <section
            className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 pb-10"
            data-testid="dea-lookup-tool"
          >
            <div className="bg-slate-50 rounded-2xl border border-slate-200 p-6 sm:p-8">
              <label
                htmlFor="npi-input"
                className="block text-sm font-medium text-slate-700 mb-2"
              >
                Enter NPI Number (10 digits)
              </label>
              <div className="flex gap-3">
                <Input
                  id="npi-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="e.g. 1234567890"
                  value={npiInput}
                  onChange={(e) =>
                    setNpiInput(e.target.value.replace(/\D/g, "").slice(0, 10))
                  }
                  onKeyDown={(e) => e.key === "Enter" && handleLookup()}
                  className="h-12 text-base font-mono tracking-wider"
                  data-testid="dea-npi-input"
                  title="Enter a 10-digit National Provider Identifier number"
                />
                <Button
                  onClick={() => handleLookup()}
                  disabled={loading}
                  className="h-12 px-6 bg-navy hover:bg-navy/90 text-white font-medium shrink-0"
                  data-testid="dea-lookup-btn"
                  title="Search NPPES database for DEA registration"
                >
                  {loading ? "Checking..." : "Look Up DEA"}
                </Button>
              </div>
            </div>

            {/* Result Card */}
            {result && (
              <div className="mt-6" data-testid="dea-result">
                {result.status === "found" && (
                  <div
                    className="rounded-xl border-2 border-emerald-400 bg-emerald-50 p-6"
                    data-testid="dea-result-found"
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <CheckCircle className="w-6 h-6 text-emerald-600" weight="fill" />
                      <span className="font-display font-semibold text-emerald-800 text-lg">
                        DEA Number Found
                      </span>
                    </div>
                    <div className="space-y-2">
                      <p className="text-slate-700">
                        <span className="text-sm text-slate-500">Provider: </span>
                        <span className="font-medium">{result.name}</span>
                      </p>
                      <p className="text-slate-700">
                        <span className="text-sm text-slate-500">NPI: </span>
                        <span className="font-mono font-medium">{result.npi}</span>
                      </p>
                      <p className="text-slate-700">
                        <span className="text-sm text-slate-500">DEA Number: </span>
                        <span className="font-mono font-bold text-emerald-700 text-lg">
                          {result.dea}
                        </span>
                      </p>
                    </div>
                    <ProviderInfoCard info={result} />
                  </div>
                )}

                {result.status === "not_found" && (
                  <div
                    className="rounded-xl border-2 border-amber-400 bg-amber-50 p-6"
                    data-testid="dea-result-not-found"
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <Warning className="w-6 h-6 text-amber-600" weight="fill" />
                      <span className="font-display font-semibold text-amber-800 text-lg">
                        No DEA Number on File
                      </span>
                    </div>
                    <div className="space-y-2">
                      <p className="text-slate-700">
                        <span className="text-sm text-slate-500">Provider: </span>
                        <span className="font-medium">{result.name}</span>
                      </p>
                      <p className="text-slate-700">
                        <span className="text-sm text-slate-500">NPI: </span>
                        <span className="font-mono font-medium">{result.npi}</span>
                      </p>
                    </div>
                    <p className="mt-4 text-sm text-amber-700 leading-relaxed">
                      No DEA number on file in NPPES. This provider may have a DEA
                      registration not listed in their NPI record. Contact the DEA
                      directly to verify.
                    </p>
                    <ProviderInfoCard info={result} />
                  </div>
                )}

                {result.status === "invalid" && (
                  <div
                    className="rounded-xl border-2 border-red-400 bg-red-50 p-6"
                    data-testid="dea-result-invalid"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <XCircle className="w-6 h-6 text-red-600" weight="fill" />
                      <span className="font-display font-semibold text-red-800 text-lg">
                        NPI Not Found
                      </span>
                    </div>
                    <p className="text-sm text-red-700">
                      NPI number not found. Please check the number and try again.
                    </p>
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {/* ============ TAB: Bulk Lookup ============ */}
        {activeTab === "bulk" && (
          <section
            className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 pb-10"
            data-testid="dea-bulk-tool"
          >
            <div className="bg-slate-50 rounded-2xl border border-slate-200 p-6 sm:p-8">
              <h2 className="font-display font-semibold text-lg text-navy mb-1">
                Bulk DEA Lookup
              </h2>
              <p className="text-sm text-slate-500 mb-4">
                For healthcare administrators — verify multiple providers at once.
                Paste up to 10 NPI numbers, one per line.
              </p>
              <textarea
                value={bulkInput}
                onChange={(e) => setBulkInput(e.target.value)}
                placeholder={"1234567890\n0987654321\n1122334455"}
                rows={5}
                className="w-full rounded-lg border border-slate-300 px-4 py-3 font-mono text-sm focus:ring-2 focus:ring-navy focus:border-transparent resize-none"
                data-testid="dea-bulk-input"
                title="Enter up to 10 NPI numbers, one per line"
              />
              <div className="flex items-center gap-3 mt-3">
                <Button
                  onClick={handleBulkLookup}
                  disabled={bulkLoading}
                  className="bg-navy hover:bg-navy/90 text-white font-medium"
                  data-testid="dea-bulk-btn"
                  title="Look up DEA numbers for all entered NPI numbers"
                >
                  {bulkLoading
                    ? `Looking up ${bulkProgress}/${bulkInput.split("\n").filter((l) => /^\d{10}$/.test(l.trim())).length}...`
                    : "Look Up All"}
                </Button>
                <span className="text-xs text-slate-400">
                  {bulkInput.split("\n").filter((l) => /^\d{10}$/.test(l.trim())).length} valid NPI(s) detected
                </span>
              </div>
            </div>

            {/* Bulk Results Table */}
            {bulkResults.length > 0 && (
              <div className="mt-6" data-testid="dea-bulk-results">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-display font-semibold text-slate-900">
                    Results ({bulkResults.length})
                  </h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={downloadCSV}
                    className="gap-2"
                    data-testid="dea-bulk-csv"
                    title="Download results as CSV file"
                  >
                    <DownloadSimple className="w-4 h-4" aria-hidden="true" />
                    Download CSV
                  </Button>
                </div>
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left px-4 py-3 font-medium text-slate-600">NPI Number</th>
                        <th className="text-left px-4 py-3 font-medium text-slate-600">Provider Name</th>
                        <th className="text-left px-4 py-3 font-medium text-slate-600 hidden sm:table-cell">Specialty</th>
                        <th className="text-left px-4 py-3 font-medium text-slate-600">DEA Number</th>
                        <th className="text-left px-4 py-3 font-medium text-slate-600">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {bulkResults.map((r, i) => (
                        <tr key={i} className="hover:bg-slate-50 transition-colors">
                          <td className="px-4 py-3 font-mono text-xs">{r.npi}</td>
                          <td className="px-4 py-3">
                            {r.slug ? (
                              <Link
                                to={`/provider/${r.npi}/${r.slug}`}
                                className="text-navy hover:text-teal font-medium transition-colors"
                                title={`View profile for ${r.nameOnly || r.name}`}
                              >
                                {r.name}
                              </Link>
                            ) : (
                              r.name
                            )}
                          </td>
                          <td className="px-4 py-3 text-slate-500 text-xs hidden sm:table-cell truncate max-w-[200px]">{r.specialty}</td>
                          <td className="px-4 py-3 font-mono text-xs">
                            {r.dea ? (
                              <span className="text-emerald-700 font-bold">{r.dea}</span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {r.status === "found" && (
                              <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs">DEA Found</Badge>
                            )}
                            {r.status === "not_found" && (
                              <Badge className="bg-amber-100 text-amber-700 border-0 text-xs">No DEA</Badge>
                            )}
                            {r.status === "invalid" && (
                              <Badge className="bg-red-100 text-red-700 border-0 text-xs">Invalid</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>
        )}

        {/* ============ TAB: Search by Name ============ */}
        {activeTab === "name" && (
          <section
            className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 pb-10"
            data-testid="dea-name-tool"
          >
            <div className="bg-slate-50 rounded-2xl border border-slate-200 p-6 sm:p-8">
              <h2 className="font-display font-semibold text-lg text-navy mb-1">
                Search by Provider Name for DEA
              </h2>
              <p className="text-sm text-slate-500 mb-4">
                Enter a provider's name to find their NPI and check for a DEA number.
              </p>
              <div className="flex gap-3">
                <Input
                  type="text"
                  placeholder="e.g. John Smith"
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleNameSearch()}
                  className="h-12 text-base"
                  data-testid="dea-name-input"
                  title="Enter a provider's first and last name"
                />
                <Button
                  onClick={handleNameSearch}
                  disabled={nameLoading}
                  className="h-12 px-6 bg-navy hover:bg-navy/90 text-white font-medium shrink-0"
                  data-testid="dea-name-btn"
                  title="Search for providers by name and check DEA status"
                >
                  {nameLoading ? "Searching..." : "Search"}
                </Button>
              </div>
            </div>

            {/* Name Search Results */}
            {nameResults.length > 0 && (
              <div className="mt-6 space-y-3" data-testid="dea-name-results">
                <h3 className="font-display font-semibold text-slate-900">
                  Top {nameResults.length} matches
                </h3>
                {nameResults.map((r, i) => (
                  <div
                    key={i}
                    className={`rounded-xl border-2 p-5 ${
                      r.status === "found"
                        ? "border-emerald-300 bg-emerald-50"
                        : "border-slate-200 bg-white"
                    }`}
                    data-testid={`dea-name-result-${i}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="space-y-1 flex-1 min-w-0">
                        <Link
                          to={`/provider/${r.npi}/${r.slug}`}
                          className="font-medium text-navy hover:text-teal transition-colors"
                          title={`View full profile for ${r.nameOnly}`}
                        >
                          {r.name}
                        </Link>
                        {r.specialty && (
                          <p className="text-sm text-teal truncate">{r.specialty}</p>
                        )}
                        {r.location && (
                          <p className="text-xs text-slate-500 flex items-center gap-1">
                            <MapPin className="w-3 h-3 shrink-0" aria-hidden="true" />
                            {r.location}
                          </p>
                        )}
                        <p className="text-xs font-mono text-slate-400">NPI: {r.npi}</p>
                      </div>
                      <div className="shrink-0 text-right">
                        {r.status === "found" ? (
                          <div>
                            <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs mb-1">DEA Found</Badge>
                            <p className="font-mono font-bold text-emerald-700 text-sm">{r.dea}</p>
                          </div>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-500 border-0 text-xs">No DEA on file</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {nameResults.length === 0 && nameLoading === false && nameInput.trim() && (
              <p className="mt-4 text-sm text-slate-500 text-center" data-testid="dea-name-no-results">
                No providers found. Try a different name.
              </p>
            )}
          </section>
        )}

        {/* Explanation Cards */}
        <section className="bg-slate-50 border-t border-b border-slate-200">
          <div
            className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12"
            data-testid="dea-info-cards"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="w-10 h-10 bg-navy/10 rounded-lg flex items-center justify-center mb-4">
                  <Pill className="w-5 h-5 text-navy" weight="bold" aria-hidden="true" />
                </div>
                <h2 className="font-display font-semibold text-lg text-slate-900 mb-3">
                  What is a DEA Number?
                </h2>
                <p className="text-sm text-slate-600 leading-relaxed">
                  A DEA (Drug Enforcement Administration) registration number is
                  required for any healthcare provider who prescribes, dispenses,
                  or administers controlled substances. It is a 9-character
                  alphanumeric code beginning with two letters followed by seven
                  digits.
                </p>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="w-10 h-10 bg-teal/10 rounded-lg flex items-center justify-center mb-4">
                  <IdentificationCard className="w-5 h-5 text-teal" weight="bold" aria-hidden="true" />
                </div>
                <h2 className="font-display font-semibold text-lg text-slate-900 mb-3">
                  What is an NPI Number?
                </h2>
                <p className="text-sm text-slate-600 leading-relaxed">
                  A National Provider Identifier (NPI) is a unique 10-digit
                  identification number issued by CMS to all healthcare providers
                  in the US. Unlike a DEA number, all providers need an NPI
                  regardless of whether they prescribe controlled substances.
                </p>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="w-10 h-10 bg-navy/10 rounded-lg flex items-center justify-center mb-4">
                  <LinkIcon className="w-5 h-5 text-navy" weight="bold" aria-hidden="true" />
                </div>
                <h2 className="font-display font-semibold text-lg text-slate-900 mb-3">
                  Why Cross-Reference Them?
                </h2>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Insurance companies, pharmacies, and healthcare organisations
                  regularly need to verify both identifiers for a single provider.
                  Some providers voluntarily list their DEA number in their NPPES
                  NPI profile, making this lookup possible.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Accordion */}
        <section
          className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12"
          data-testid="dea-faq-section"
        >
          <h2 className="font-display font-bold text-2xl text-slate-900 mb-6 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-3">
            {FAQ_DATA.map((faq, i) => (
              <details
                key={i}
                className="group border border-slate-200 rounded-xl bg-white"
                data-testid={`dea-faq-${i}`}
              >
                <summary className="flex items-center justify-between p-5 cursor-pointer select-none hover:bg-slate-50 transition-colors rounded-xl">
                  <span className="font-medium text-slate-900 text-sm sm:text-base pr-4">
                    {faq.q}
                  </span>
                  <CaretRight className="w-4 h-4 text-slate-400 group-open:rotate-90 transition-transform flex-shrink-0" aria-hidden="true" />
                </summary>
                <div className="px-5 pb-5 text-sm text-slate-600 leading-relaxed">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>
        </section>

        {/* Related Lookups */}
        <section className="bg-slate-50 border-t border-slate-200">
          <div
            className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12"
            data-testid="dea-related-links"
          >
            <h2 className="font-display font-bold text-xl text-slate-900 mb-6 text-center">
              Related Lookups
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
              <Link
                to="/"
                className="group flex items-center gap-3 bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md hover:border-teal/30 transition-all"
                data-testid="related-npi-lookup"
                title="Look up any healthcare provider by their NPI number"
              >
                <div className="w-10 h-10 bg-navy/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <House className="w-5 h-5 text-navy" aria-hidden="true" />
                </div>
                <p className="font-medium text-slate-900 group-hover:text-teal transition-colors text-sm">
                  Look up by NPI Number
                </p>
              </Link>

              <Link
                to="/search"
                className="group flex items-center gap-3 bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md hover:border-teal/30 transition-all"
                data-testid="related-name-search"
                title="Search for healthcare providers by name, specialty, or location"
              >
                <div className="w-10 h-10 bg-teal/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-teal" aria-hidden="true" />
                </div>
                <p className="font-medium text-slate-900 group-hover:text-teal transition-colors text-sm">
                  Search by Provider Name
                </p>
              </Link>

              <Link
                to="/specialties"
                className="group flex items-center gap-3 bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md hover:border-teal/30 transition-all"
                data-testid="related-taxonomy"
                title="Browse all healthcare specialties and taxonomy codes"
              >
                <div className="w-10 h-10 bg-navy/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Tag className="w-5 h-5 text-navy" aria-hidden="true" />
                </div>
                <p className="font-medium text-slate-900 group-hover:text-teal transition-colors text-sm">
                  Taxonomy Code Lookup
                </p>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default DeaNpiLookup;
